/**
 * PRDNC Currency Display Switcher
 * -----------------------------------------------------------------
 * IMPORTANT: This is a DISPLAY-ONLY converter. It rewrites visible
 * Naira prices into an estimated foreign-currency equivalent so
 * international shoppers can browse comfortably. The actual charge
 * at checkout always happens in Nigerian Naira (NGN) — this is a
 * Shopify Payments limitation (not available in Nigeria) and cannot
 * be changed by front-end code. The UI is explicit about this so
 * customers are never misled.
 * -----------------------------------------------------------------
 */
(function () {
  'use strict';

  var STORE_CURRENCY = 'NGN';

  var CURRENCIES = {
    NGN: { symbol: '₦', label: 'NGN — Nigerian Naira', decimals: 0 },
    USD: { symbol: '$', label: 'USD — US Dollar', decimals: 2 },
    GBP: { symbol: '£', label: 'GBP — British Pound', decimals: 2 },
    ZAR: { symbol: 'R', label: 'ZAR — South African Rand', decimals: 2 },
    GHS: { symbol: 'GH₵', label: 'GHS — Ghanaian Cedi', decimals: 2 }
  };

  var FALLBACK_NGN_PER_UNIT = {
    NGN: 1,
    USD: 1375,
    GBP: 1808,
    ZAR: 83.3,
    GHS: 122.8
  };

  var COUNTRY_TO_CURRENCY = {
    NG: 'NGN', US: 'USD', GB: 'USD', ZA: 'ZAR', GH: 'GHS'
  };
  COUNTRY_TO_CURRENCY.GB = 'GBP';

  var STORAGE_KEY = 'prdnc_currency';
  var RATES_CACHE_KEY = 'prdnc_rates_cache';
  var RATES_CACHE_TTL = 1000 * 60 * 60 * 12;

  var state = {
    currency: STORE_CURRENCY,
    rates: FALLBACK_NGN_PER_UNIT
  };

  function getStoredCurrency() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  function setStoredCurrency(code) {
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch (e) {}
  }

  function loadCachedRates() {
    try {
      var raw = localStorage.getItem(RATES_CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (Date.now() - parsed.ts > RATES_CACHE_TTL) return null;
      return parsed.rates;
    } catch (e) {
      return null;
    }
  }

  function cacheRates(rates) {
    try {
      localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ ts: Date.now(), rates: rates }));
    } catch (e) {}
  }

  function fetchLiveRates() {
    var cached = loadCachedRates();
    if (cached) {
      state.rates = cached;
      convertAllPrices();
    }
    fetch('https://open.er-api.com/v6/latest/NGN')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (!data || !data.rates) return;
        var next = { NGN: 1 };
        Object.keys(CURRENCIES).forEach(function (code) {
          if (code === 'NGN') return;
          var ngnToTarget = data.rates[code];
          if (ngnToTarget && ngnToTarget > 0) {
            next[code] = 1 / ngnToTarget;
          } else {
            next[code] = FALLBACK_NGN_PER_UNIT[code];
          }
        });
        state.rates = next;
        cacheRates(next);
        convertAllPrices();
      })
      .catch(function () {});
  }

  function detectCountry(callback) {
    fetch('https://ipapi.co/json/')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        callback(data && data.country_code ? data.country_code : null);
      })
      .catch(function () {
        callback(null);
      });
  }

  function formatMoney(amountNGN, code) {
    var meta = CURRENCIES[code];
    var rate = state.rates[code] || FALLBACK_NGN_PER_UNIT[code];
    var converted = amountNGN / rate;
    var fixed = converted.toFixed(meta.decimals);
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return meta.symbol + parts.join('.');
  }

  function parseNaira(text) {
    var cleaned = text.replace(/[^\d.]/g, '');
    if (!cleaned) return null;
    var value = parseFloat(cleaned);
    return isNaN(value) ? null : value;
  }

  var PRICE_NODE_SELECTOR =
    '.price-item, .price__regular .price-item, .price__sale .price-item, ' +
    '.totals__subtotal-value, .cart-item__price, .product__price, ' +
    '[data-prdnc-price]';

  function walkAndTag() {
    var candidates = document.querySelectorAll(PRICE_NODE_SELECTOR);
    candidates.forEach(function (el) {
      if (el.dataset.prdncOriginal) return;
      var text = el.textContent;
      if (text.indexOf('₦') === -1) return;
      var amount = parseNaira(text);
      if (amount === null) return;
      el.dataset.prdncOriginal = String(amount);
      el.dataset.prdncPrice = 'true';
    });
  }

  var isConverting = false;

  function convertAllPrices() {
    if (isConverting) return;
    isConverting = true;
    walkAndTag();
    var tagged = document.querySelectorAll('[data-prdnc-price]');
    tagged.forEach(function (el) {
      var amount = parseFloat(el.dataset.prdncOriginal);
      if (isNaN(amount)) return;
      var next = state.currency === 'NGN'
        ? formatMoney(amount, 'NGN')
        : formatMoney(amount, state.currency) + ' est.';
      if (el.textContent !== next) {
        el.textContent = next;
      }
    });
    setTimeout(function () { isConverting = false; }, 0);
  }

  function buildSwitcher() {
    var wrap = document.createElement('div');
    wrap.className = 'prdnc-currency-switcher';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'prdnc-currency-switcher__button';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.textContent = state.currency;

    var panel = document.createElement('div');
    panel.className = 'prdnc-currency-switcher__panel';
    panel.hidden = true;

    var note = document.createElement('p');
    note.className = 'prdnc-currency-switcher__note';
    note.textContent = 'Estimated prices for browsing only. All orders are charged in Nigerian Naira (₦) at checkout.';
    panel.appendChild(note);

    var list = document.createElement('ul');
    list.setAttribute('role', 'listbox');
    Object.keys(CURRENCIES).forEach(function (code) {
      var li = document.createElement('li');
      var opt = document.createElement('button');
      opt.type = 'button';
      opt.textContent = CURRENCIES[code].label;
      opt.dataset.code = code;
      if (code === state.currency) opt.setAttribute('aria-selected', 'true');
      opt.addEventListener('click', function () {
        state.currency = code;
        setStoredCurrency(code);
        button.textContent = code;
        panel.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        convertAllPrices();
      });
      li.appendChild(opt);
      list.appendChild(li);
    });
    panel.appendChild(list);

    button.addEventListener('click', function () {
      var isHidden = panel.hidden;
      panel.hidden = !isHidden;
      button.setAttribute('aria-expanded', String(isHidden));
    });

    document.addEventListener('click', function (e) {
      if (!wrap.contains(e.target)) {
        panel.hidden = true;
        button.setAttribute('aria-expanded', 'false');
      }
    });

    wrap.appendChild(button);
    wrap.appendChild(panel);
    return wrap;
  }

  function mountSwitcher() {
    var header = document.querySelector('.header__icons, header-actions, .header-wrapper');
    var target = document.querySelector('.header__icons') || document.querySelector('header-actions');
    var switcherEl = buildSwitcher();
    if (target) {
      target.parentNode.insertBefore(switcherEl, target);
    } else if (header) {
      header.appendChild(switcherEl);
    } else {
      document.body.appendChild(switcherEl);
    }
  }

  function init() {
    var stored = getStoredCurrency();
    if (stored && CURRENCIES[stored]) {
      state.currency = stored;
      mountSwitcher();
      fetchLiveRates();
      convertAllPrices();
    } else {
      mountSwitcher();
      fetchLiveRates();
      detectCountry(function (countryCode) {
        var guess = COUNTRY_TO_CURRENCY[countryCode] || (countryCode && countryCode !== 'NG' ? 'USD' : 'NGN');
        state.currency = guess;
        var btn = document.querySelector('.prdnc-currency-switcher__button');
        if (btn) btn.textContent = guess;
        convertAllPrices();
      });
    }

    var debounceTimer = null;
    var observer = new MutationObserver(function () {
      if (isConverting) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(convertAllPrices, 150);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * PRDNC Sticky Add to Cart + Recently Viewed
 * -----------------------------------------------------------------
 * Sticky bar: mirrors the real on-page Add to Cart button (clicks it
 * directly rather than reimplementing cart logic) so it always stays
 * in sync with variant selection, stock status, etc.
 *
 * Recently Viewed: tracks real browsing history in localStorage.
 * No fabricated data — only products the visitor actually viewed.
 * -----------------------------------------------------------------
 */
(function () {
  'use strict';

  function initStickyAtc() {
    var realButton = document.querySelector('product-form button[name="add"], form[action*="/cart/add"] button[name="add"]');
    if (!realButton) return;

    var titleEl = document.querySelector('.product__title h1, .product__title');
    var priceEl = document.querySelector('.price-item--regular, .price-item');
    if (!titleEl || !priceEl) return;

    var bar = document.createElement('div');
    bar.className = 'prdnc-sticky-atc';
    bar.innerHTML =
      '<div class="prdnc-sticky-atc__info">' +
        '<strong>' + titleEl.textContent.trim() + '</strong>' +
        '<span>' + priceEl.textContent.trim() + '</span>' +
      '</div>';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button';
    btn.textContent = realButton.disabled ? realButton.textContent.trim() : 'Add to Cart';
    btn.disabled = realButton.disabled;
    btn.addEventListener('click', function () {
      realButton.click();
    });
    bar.appendChild(btn);
    document.body.appendChild(bar);

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { threshold: 0 });
    observer.observe(realButton);

    var syncObserver = new MutationObserver(function () {
      btn.disabled = realButton.disabled;
      btn.textContent = realButton.textContent.trim() || 'Add to Cart';
    });
    syncObserver.observe(realButton, { attributes: true, childList: true, characterData: true, subtree: true });
  }

  var RECENTLY_VIEWED_KEY = 'prdnc_recently_viewed';
  var MAX_ITEMS = 8;
  var MAX_DISPLAY = 4;

  function initRecentlyViewed() {
    var section = document.getElementById('prdnc-recently-viewed');
    if (!section) return;

    var current = {
      handle: section.dataset.currentHandle,
      title: section.dataset.currentTitle,
      image: section.dataset.currentImage,
      price: section.dataset.currentPrice,
      url: section.dataset.currentUrl
    };

    var stored = [];
    try {
      stored = JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
    } catch (e) {
      stored = [];
    }

    var toShow = stored.filter(function (item) { return item.handle !== current.handle; }).slice(0, MAX_DISPLAY);

    if (toShow.length > 0) {
      var grid = document.getElementById('prdnc-recently-viewed-grid');
      grid.innerHTML = toShow.map(function (item) {
        return '<a class="prdnc-recently-viewed__item" href="' + item.url + '">' +
          '<img src="' + item.image + '" loading="lazy" alt="' + item.title + '">' +
          '<span>' + item.title + '</span>' +
          '</a>';
      }).join('');
      section.hidden = false;
    }

    var updated = stored.filter(function (item) { return item.handle !== current.handle; });
    updated.unshift(current);
    updated = updated.slice(0, MAX_ITEMS);
    try {
      localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(updated));
    } catch (e) {}
  }

  function init() {
    initStickyAtc();
    initRecentlyViewed();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
